/**
 * The daily challenge, as reactive state.
 *
 * The shape follows `account.svelte.ts`'s own rules, because the same
 * constraint applies: **nothing here is on the path of a guest playing a
 * game.** The Play tab's Daily card reads this store and renders
 * whatever it finds, but a run that never asked this module a question is
 * still a fully playable Sprint — the card is a convenience over the daily
 * seed, not a gate in front of it.
 *
 * ## Why this is a separate store from `account`
 *
 * `GET /api/daily` works signed out (a guest sees the board), so this store's
 * `status` has no `guest`/`signed-in` distinction of its own — it tracks
 * whether *the daily itself* is reachable, and `account.status` is still
 * where a screen asks whether a *result* can be posted. Two questions, two
 * stores, the same way `account` and `admin` stay apart.
 */
import { fetchDaily, submitDailyResult } from './daily-io.js';
import type { DailyInfo, DailySubmission } from './daily-io.js';

export type DailyStatus =
  /** The probe has not answered yet. */
  | 'unknown'
  | 'ready'
  /** No database on this server, or it could not be reached — including offline. */
  | 'unavailable';

interface DailyState {
  status: DailyStatus;
  info: DailyInfo | null;
  /** Set only while a result is being posted, so the card can disable its own button. */
  submitting: boolean;
  /** A refusal worth showing after a post — stale seed, already played, out of bounds. Cleared by the next attempt. */
  error: string;
}

export const daily: DailyState = $state({
  status: 'unknown',
  info: null,
  submitting: false,
  error: '',
});

/**
 * Ask the server about today. Safe to call at any time; safe to fail.
 *
 * Skipped entirely while the browser already says it is offline, for the
 * same reason `account.svelte.ts#refresh` skips its own probe: a failed
 * request on every cold start of an installed PWA is noise somebody will
 * eventually chase, and `navigator.onLine === false` answers the question
 * for free.
 */
export async function refreshDaily(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    daily.status = 'unavailable';
    return;
  }
  const result = await fetchDaily();
  if (result.status === 'ok') {
    daily.status = 'ready';
    daily.info = result.value;
    return;
  }
  // `signed-out` cannot happen here — the route never refuses a guest — so
  // every non-`ok` outcome means the same thing to this card: there is
  // nothing to show.
  daily.info = null;
  daily.status = 'unavailable';
}

/**
 * Post a finished daily run and fold the server's fresh standing back into
 * the store, so the card and the results screen read the same rank without
 * a second round trip.
 *
 * Returns `true` on success. A caller that gets `false` reads `daily.error`
 * for what to show — `daily.svelte.ts` never throws for a reason a player
 * caused, the same rule `account-io.ts` states for itself.
 */
export async function postDailyResult(submission: DailySubmission): Promise<boolean> {
  daily.submitting = true;
  daily.error = '';
  try {
    const result = await submitDailyResult(submission);
    if (result.status !== 'ok') {
      daily.error = messageFor(result);
      return false;
    }
    if (daily.info !== null) {
      daily.info = {
        ...daily.info,
        played: true,
        yourResult: result.value.result,
        yourRank: result.value.rank,
        leaderboard: result.value.leaderboard,
      };
    }
    return true;
  } finally {
    daily.submitting = false;
  }
}

function messageFor(result: Awaited<ReturnType<typeof submitDailyResult>>): string {
  if (result.status === 'refused') return result.message;
  if (result.status === 'signed-out') return 'Sign in to post a daily result.';
  return 'Could not reach the server. Your run still counts locally.';
}
